import {
  FieldValues,
  FormProvider,
  useForm,
  UseFormProps,
} from 'react-hook-form';
import { camelizeKeys } from 'humps';
import { useEffect, useState } from 'react';
import { omit } from 'lodash';
import posthog from 'posthog-js';
import { FORM_ERROR } from './types';
import { camelizeKey } from '../../../helpers';
import { useLocalStorageTTL } from './hooks/useLocalStorageTTL';
import { RestoreFormDataModal } from './RestoreFormDataModal';
import { useInLocalStorageTTL } from './hooks/useInLocalStorageTTL';

/**
 * FormProps
 * @public
 */
export type FormProps<
  T extends FieldValues = any,
  R = any,
> = UseFormProps<T> & {
  /**
   * Cache key for form
   * When provided, the form will be cached in local storage before submit form. After submit, cache will be removed.
   */
  cacheKey?: string;

  /**
   * Cache time in milliseconds
   */
  cacheTime?: number;

  /**
   * Cache exclude fields
   */
  cacheExclude?: string[];

  /**
   * Function to call when form is submitted
   * @param data - form data
   */
  onSubmit: (data: T) => R | Promise<R>;

  /**
   * Function to call when form is submitted with error
   * @param error
   * @param data
   */
  onError?: (error: any, data: T) => void;

  /**
   * Function to call when form is submitted successfully
   * @param data
   */
  onSuccess?: (data: T) => void;

  /**
   * Form children
   */
  children?: React.ReactNode;

  /**
   * Render function
   * @param form
   */
  render?: (form: ReturnType<typeof useForm>) => React.ReactNode;

  /**
   * Form hook to override default form hook inside in Form component
   */
  form?: ReturnType<typeof useForm<any, any>>;

  /**
   * Style for form
   */
  style?: React.CSSProperties;
};

/**
 * Form component
 *
 * @example
 * ```tsx
 * <Form onSubmit={onSubmit}>
 *   <FormError />
 *   <TextField name="name" />
 *   <FormButton type="submit">Submit</Button>
 * </Form>
 * ```
 *
 * @example
 * ```tsx
 * const form = useForm();
 *
 * return (
 *  <Form form={form} onSubmit={onSubmit}>
 *    <FormError />
 *    <TextField name="name" />
 *    <FormButton type="submit">Submit</Button>
 *  </Form>
 * );
 * ```
 *
 * @public
 * @param cacheKey - cache key for form
 * @param cacheTime - cache time in milliseconds
 * @param cacheExclude - cache exclude fields
 * @param onSubmit - function to call when form is submitted
 * @param onError - function to call when form is submitted with error
 * @param onSuccess - function to call when form is submitted successfully
 * @param children - form children
 * @param render - render function
 * @param form - form hook
 * @param formProps - form props
 * @param style - form style
 * @see FormProps
 * @see useForm
 * @see FormError
 * @see FormButton
 */
export const Form: React.FC<FormProps> = ({
  cacheKey,
  cacheTime,
  cacheExclude = [],
  onSubmit,
  onSuccess,
  onError,
  children,
  render,
  form,
  style,
  ...formProps
}) => {
  const [cache, setCache, removeCache] = useLocalStorageTTL(
    cacheKey,
    cacheTime,
    form ? form.watch() : formProps.defaultValues,
  );
  const inCache = useInLocalStorageTTL(cacheKey);
  const [openRestore, setOpenRestore] = useState<boolean>(inCache);

  const insideController = useForm({ ...formProps });
  const insideForm = form || insideController;
  const insideFormData = insideForm.watch();

  /**
   * Set cache when form is dirty
   */
  useEffect(() => {
    if (cacheKey) {
      if (insideForm.formState.isDirty) {
        setCache(omit(insideFormData, cacheExclude));
      }
    } else {
      removeCache();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, JSON.stringify(insideFormData)]);

  /**
   * Restore form data from cache
   */
  const restoreDataFromCache = () => {
    insideForm.reset(cache);
    posthog.capture('FormRestoreDataFromCache');
    setOpenRestore(false);
  };

  /**
   * Overwrite onSubmit function
   * @param formData
   */
  const overwriteOnSubmit = async (formData: unknown) => {
    try {
      await onSubmit(formData);
      onSuccess?.(formData);
      removeCache();
    } catch (error: any) {
      const { response } = error || {};
      const data: any = response?.data || {};
      const status = response?.status || 0;

      if (status >= 400) {
        /**
         * Set general error message when not provided other errors
         */
        if (data?.message && !data?.errors) {
          insideForm.setError(FORM_ERROR, {
            message: data.message,
          });
        }

        const uuidRegex =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

        /**
         * Change key to camelCase except uuid (save uuid in original format)
         */
        const errors = camelizeKeys(data.errors || {}, (key, convert) =>
          key.split('.').reduce((acc, item) => {
            const newKey = uuidRegex.test(item)
              ? item
              : camelizeKey(item, convert);

            return [acc, newKey].join('.');
          }, ''),
        );

        /**
         * Set field errors
         */
        Object.entries(errors).forEach(([key, value]: [string, any]) => {
          insideForm.setError(key, {
            type: 'api',
            message: value[0],
          });
        });
      }
      onError?.(error, formData);
    }
  };

  return (
    <>
      <RestoreFormDataModal
        opened={openRestore}
        onRestore={() => restoreDataFromCache()}
        onClose={() => setOpenRestore(false)}
      />
      <FormProvider {...insideForm}>
        <form
          onSubmit={ev => {
            ev.stopPropagation();
            return insideForm.handleSubmit(overwriteOnSubmit)(ev);
          }}
          style={style}
        >
          {render?.(insideForm) || children}
        </form>
      </FormProvider>
    </>
  );
};
